import { ListButton } from "@refinedev/antd";
import {
  Input,
  Select,
  Col,
  Row,
  Spin,
  Switch,
  Typography,
  message,
  Form,
  Timeline,
} from "antd";
import { useNavigation, useTranslate } from "@refinedev/core";
import {
  CheckOutlined,
  FolderOpenOutlined,
  HistoryOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import { Flex, Button, theme } from "antd";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { axiosInstance } from "@/utilities";
import {
  API_URL_GET_TEMPLATE,
  API_URL_GET_TEMPLATE_VARIABLES,
  API_URL_POST_TEMPLATE,
} from "@/services/XCONS_ENV";
import { useLocation } from "react-router-dom";
import JoditEditor from "jodit-react";

const { Option } = Select;
const { Title, Text } = Typography;

interface OptionItem {
  label: string;
  value: string;
  group: string;
}

interface GroupedOption {
  label: React.ReactNode;
  title: string;
  options: { label: React.ReactNode; value: string }[];
}

export const StoresTemplatesPanel = () => {
  const [structureLoading, setStructureLoading] = useState<boolean>(false);
  const [templateData, setTemplateData] = useState<any>([]);
  const [templateVariables, setTemplateVariables] = useState<OptionItem[]>([]);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [value, setValue] = useState<string>("");
  const editorValueRef = useRef<string>("");

  const t = useTranslate();
  const { token } = theme.useToken();
  const { push } = useNavigation();
  const [form] = Form.useForm();
  const location = useLocation();

  const record = location.state?.record;

  const { smartMenuName } = useContext(SmartMenuContext);

  // ID del seller desde el LocalStorage
  const sellerCode = encodeURIComponent(
    smartMenuName?.replace(/"/g, "").toLowerCase(),
  );

  useEffect(() => {
    if (record?.text) {
      editorValueRef.current = record.text;
    }
  }, [record]);

  useEffect(() => {
    if (record) {
      form?.setFieldsValue({
        template_name: record.name,
        channel: record.channel || "email",
        subject: record.subject,
        styles: record.styles,
        template_variable: record.text || editorValueRef?.current || "",
      });
    }
  }, [record, form]);

  const handleBack = () => {
    push(`/stores/templates/${sellerCode}`);
  };

  const getTemplateVariables = async () => {
    setStructureLoading(true);
    try {
      const res = await axiosInstance.get(`${API_URL_GET_TEMPLATE_VARIABLES}`);
      setTemplateVariables(res.data);
    } catch (error) {
      console.error(error);
      message.error("Error al cargar el template");
    } finally {
      setStructureLoading(false);
    }
  };

  const getTemplates = async () => {
    if (record?.templateId) {
      setStructureLoading(true);
      try {
        // const res = await axiosInstance.get(
        //   `${API_URL_GET_TEMPLATE}/${record.templateId}`,
        // );
        setTemplateData(record);
        form.setFieldsValue(record);
      } catch (error) {
        console.error(error);
        message.error("Error al cargar el template");
      } finally {
        setStructureLoading(false);
      }
    } else {
      form.resetFields();
    }
  };

  useEffect(() => {
    getTemplateVariables();
  }, []);

  const handleSave = async (values: any) => {
    setStructureLoading(true);

    const data = {
      name: values?.template_name,
      subject: values?.subject,
      text: values?.template_variable,
      styles: values?.styles || "",
    };

    try {
      if (record?.templateId) {
        await axiosInstance.put(
          `${API_URL_GET_TEMPLATE}/${record.templateId}`,
          data,
        );

        message.success(t("quotes.message.templateSaved"));
      } else {
        await axiosInstance.post(
          `${API_URL_POST_TEMPLATE}/${sellerCode}`,
          data,
        );

        message.success(t("quotes.message.templateSaved"));
      }
      handleBack();
    } catch (error: any) {
      console.error(error);
      message.error(
        t("notifications.error", { statusCode: error.message }) ||
          t("notifications.error"),
      );
    } finally {
      setStructureLoading(false);
    }
  };

  const handleArchive = async (values: any) => {
    setStructureLoading(true);

    const data = {
      name: encodeURIComponent(values?.template_name),
      subject: encodeURIComponent(values?.subject),
      text: encodeURIComponent(editorValueRef?.current),
      styles: encodeURIComponent(values?.styles) || "",
    };

    try {
      await axiosInstance.put(
        `${API_URL_GET_TEMPLATE}/${record.templateId}`,
        data,
      );

      message.success(t("quotes.message.templateArchived"));

      handleBack();
    } catch (error: any) {
      console.error(error);
      message.error(error.message || "Error al guardar el template");
    } finally {
      setStructureLoading(false);
    }
  };

  const handleSelect = (selectedValue: string) => {
    setValue((prevValue) => `${prevValue} ${selectedValue}`);
    editorValueRef.current = `${editorValueRef.current} ${selectedValue}`;
  };

  const handleEditorChange = (newValue: string) => {
    editorValueRef.current = newValue;
    form.setFieldsValue({ template_variable: newValue });
  };

  const groupOptions = (options: OptionItem[]): GroupedOption[] => {
    const uniqueOptions = options.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.value === item.value),
    );

    const grouped = uniqueOptions.reduce<Record<string, GroupedOption>>(
      (acc, item) => {
        if (!acc[item.group]) {
          acc[item.group] = {
            label: <span>{item.group}</span>,
            title: item.group,
            options: [],
          };
        }
        acc[item.group].options.push({
          label: item.label,
          value: item.value,
        });
        return acc;
      },
      {},
    );

    return Object.values(grouped);
  };

  return (
    <Flex vertical>
      <Flex align="center" style={{ marginBottom: "40px" }}>
        <ListButton onClick={handleBack} hideText icon={<LeftOutlined />} />
        <Title style={{ marginBottom: 0, marginLeft: "10px" }} level={3}>
          {record ? record.name : t("quotes.label.titleNewTemplate")}
        </Title>
      </Flex>
      <Spin spinning={structureLoading}>
        <Row gutter={12} wrap justify="center" style={{ width: "100%" }}>
          <Col xs={24} md={24} lg={24}>
            <Form form={form} layout="vertical" onFinish={handleSave}>
              <Form.Item
                label={
                  <Flex
                    align="center"
                    justify="space-between"
                    style={{ width: "100%" }}
                  >
                    <span
                      style={{
                        color: token.colorTextTertiary,
                        fontWeight: "bold",
                        width: "100%",
                      }}
                    >
                      {t("quotes.label.templateName")}
                    </span>
                    <Switch
                      defaultChecked
                      checkedChildren={t("actions.enabled")}
                      unCheckedChildren={t("actions.disabled")}
                    />
                  </Flex>
                }
                name="template_name"
                rules={[{ required: true, message: "Este campo es requerido" }]}
              >
                <Input placeholder="Nombre del template" />
              </Form.Item>

              <Form.Item
                style={{ maxWidth: 200 }}
                label={
                  <span
                    style={{
                      color: token.colorTextTertiary,
                      fontWeight: "bold",
                    }}
                  >
                    {t("quotes.label.templateChannel")}
                  </span>
                }
                name="channel"
                rules={[{ required: true, message: "Este campo es requerido" }]}
              >
                <Select
                  allowClear
                  placeholder="Selecciona un canal"
                  options={[
                    { label: "HTML email", value: "email" },
                    // { label: "WhatsApp", value: "whatsapp" },
                  ]}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span
                    style={{
                      color: token.colorTextTertiary,
                      fontWeight: "bold",
                    }}
                  >
                    {t("quotes.label.templateSubject")}
                  </span>
                }
                name="subject"
                rules={[{ required: true, message: "Este campo es requerido" }]}
              >
                <Input placeholder="Asunto del template" />
              </Form.Item>

              <Form.Item
                label={
                  <Flex
                    align="center"
                    justify="space-between"
                    style={{ width: "100%" }}
                  >
                    <span
                      style={{
                        color: token.colorTextTertiary,
                        fontWeight: "bold",
                      }}
                    >
                      {t("quotes.label.templateContent")}
                    </span>
                    <Select
                      style={{ width: 300 }}
                      placeholder="Selecciona una variale"
                      onSelect={handleSelect}
                      options={groupOptions(templateVariables)}
                    />
                  </Flex>
                }
                name="template_variable"
                rules={[{ required: true, message: "Este campo es requerido" }]}
              >
                <JoditEditor
                  value={editorValueRef?.current}
                  onChange={handleEditorChange}
                  config={{
                    height: 500,
                    theme: "light",
                    readonly: false,
                    language: "es",
                    // toolbar: channel === "whatsapp" ? false : true,
                  }}
                />
                <div
                  style={{
                    display: "none",
                    border: "1px solid #ddd",
                    padding: 10,
                    borderRadius: 4,
                  }}
                  dangerouslySetInnerHTML={{ __html: editorValueRef.current }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span
                    style={{
                      color: token.colorTextTertiary,
                      fontWeight: "bold",
                    }}
                  >
                    {t("quotes.label.templateStyle")}
                  </span>
                }
                name="styles"
              >
                <Input.TextArea rows={4} placeholder="Estilos del template" />
              </Form.Item>

              {/* <Flex vertical justify="start" style={{ marginTop: "40px" }}>
                <Title
                  style={{ marginBottom: 0, marginLeft: "10px" }}
                  level={3}
                >
                  <Flex gap={8}>
                    <HistoryOutlined />
                    History
                  </Flex>
                </Title>
              </Flex> */}

              <Flex justify="space-between" style={{ marginTop: "40px" }}>
                <Button
                  onClick={handleArchive}
                  iconPosition="end"
                  icon={<FolderOpenOutlined />}
                >
                  {t("quotes.button.archive")}
                </Button>
                <Flex gap={8}>
                  <Button danger onClick={handleBack}>
                    {t("buttons.cancel")}
                  </Button>
                  {/* <Button onClick={handlePreview}>Preview</Button> */}
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<CheckOutlined />} iconPosition="end"
                  >
                    {t("quotes.button.save")}
                  </Button>
                </Flex>
              </Flex>
            </Form>
          </Col>
        </Row>
      </Spin>
    </Flex>
  );
};
